import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { userModel } from 'entities/user';
import { userActions } from 'entities/user/model';
import {
  checkRequestStatusEquality,
  flexHelper,
  Icon,
  RequestStatus,
  routes,
  uiSelector,
  useShallowSelector,
} from 'shared';

import { Countries, initialState, KycFormFields, KycFormFlag, validationSchema } from '..';

export const KycForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
    resetField,
    watch,
  } = useForm<KycFormFields>({
    mode: 'all',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    resolver: yupResolver(validationSchema),
    defaultValues: initialState,
  });
  const participateValue = watch('isParticipate');
  const residentValue = watch('isResident');
  const activeCountry = watch('country');

  const isSaveButtonEnabled = useMemo(() => {
    return isValid || (participateValue === KycFormFlag.no && residentValue === KycFormFlag.no);
  }, [isValid, participateValue, residentValue]);

  const uiState = useShallowSelector(uiSelector);
  const { address } = useShallowSelector(userModel.selectors.selectUserWeb3Info);

  const isSendingKycSuccess = checkRequestStatusEquality(uiState[userActions.saveKyc?.type], RequestStatus.SUCCESS);

  const handleSubmitForm = (formFields: KycFormFields) => {
    const data = {
      address,
      ...(residentValue === KycFormFlag.yes && {
        userInfo: {
          firstName: formFields.firstName,
          lastName: formFields.lastName,
          email: formFields.email,
          country: formFields.country,
          ...(activeCountry === Countries.usa ? { state: formFields.state } : { province: formFields.province }),
          city: formFields.city,
          fullAddress: formFields.fullAddress,
          zipCode: formFields.zipCode,
        },
      }),
      ...(participateValue === KycFormFlag.yes && { externalTransactionId: formFields.externalTransactionId }),
    };
    dispatch(userActions.saveKyc(data));
  };

  useEffect(() => {
    if (isSendingKycSuccess) {
      navigate(routes.swap.path);
    }
  }, [isSendingKycSuccess, navigate]);
  return (
    <Box component="form" onSubmit={handleSubmit(handleSubmitForm)}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Stack spacing={2} sx={{ maxWidth: { xs: '100%', md: 564 }, width: '100%' }}>
          <Accordion sx={{ p: 3 }} expanded={participateValue === KycFormFlag.yes}>
            <AccordionSummary
              sx={{
                '.MuiAccordionSummary-content': { ...flexHelper('flex-start', 'flex-start', 'column') },
              }}
            >
              <Typography variant="button">Did you participate in the Mogul Access Pass Sale?</Typography>
              <RadioGroup row sx={{ '.MuiFormControlLabel-root': { m: 0, mt: '18px' } }}>
                <FormControlLabel
                  onClick={() => setValue('isParticipate', KycFormFlag.yes)}
                  value={KycFormFlag.yes}
                  control={<Radio sx={{ mr: 1.5 }} />}
                  label="Yes"
                />
                <FormControlLabel
                  onClick={() => setValue('isParticipate', KycFormFlag.no)}
                  value={KycFormFlag.no}
                  control={<Radio sx={{ mr: 1.5, ml: 2.5 }} />}
                  label="No"
                />
              </RadioGroup>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                label={
                  errors.externalTransactionId
                    ? errors.externalTransactionId.message
                    : 'If so, please provide the transaction ID (TXID)'
                }
                {...register('externalTransactionId')}
                error={!!errors.externalTransactionId}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion sx={{ p: 3 }} expanded={residentValue === KycFormFlag.yes}>
            <AccordionSummary
              sx={{
                '.MuiAccordionSummary-content': { ...flexHelper('flex-start', 'flex-start', 'column') },
              }}
            >
              <Typography variant="button">Are you a Canadian or US citizen and/or resident?</Typography>
              <RadioGroup row sx={{ '.MuiFormControlLabel-root': { m: 0, mt: '18px' } }}>
                <FormControlLabel
                  onClick={() => setValue('isResident', KycFormFlag.yes)}
                  value={KycFormFlag.yes}
                  control={<Radio sx={{ mr: 1.5 }} />}
                  label="Yes"
                />
                <FormControlLabel
                  onClick={() => setValue('isResident', KycFormFlag.no)}
                  value={KycFormFlag.no}
                  control={<Radio sx={{ mr: 1.5, ml: 2.5 }} />}
                  label="No"
                />
              </RadioGroup>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography className="gray">If so, please provide the proper contact information below :</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    {...register('firstName')}
                    label={errors.firstName ? errors.firstName.message : 'First name'}
                    error={!!errors.firstName}
                  />
                  <TextField
                    {...register('lastName')}
                    label={errors.lastName ? errors.lastName.message : 'Last name'}
                    error={!!errors.lastName}
                  />
                </Stack>
                <TextField
                  {...register('email')}
                  label={errors.email ? errors.email.message : 'Email'}
                  error={!!errors.email}
                />
              </Stack>
              <Stack spacing={2} sx={{ mt: 3 }}>
                <Typography className="gray">Country</Typography>
                <Tabs
                  value={activeCountry}
                  onChange={(_, value) => {
                    resetField(activeCountry === Countries.usa ? 'state' : 'province');
                    setValue('country', value);
                  }}
                >
                  <Tab value={Countries.usa} icon={<Icon type="usa-flag" />} label="USA" />
                  <Tab value={Countries.canada} icon={<Icon type="canada-flag" />} label="Canada" />
                </Tabs>
                <TextField
                  label={activeCountry === Countries.usa ? 'State' : 'Province'}
                  {...register(activeCountry === Countries.usa ? 'state' : 'province')}
                  error={activeCountry === Countries.usa ? !!errors.state : !!errors.province}
                  sx={{ maxWidth: { xs: '100%', md: 'calc(50% - 8px)' } }}
                />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    {...register('city')}
                    label={errors.city ? errors.city.message : 'City'}
                    error={!!errors.city}
                  />
                  <TextField
                    {...register('zipCode')}
                    label={errors.zipCode ? errors.zipCode.message : 'Zip code'}
                    error={!!errors.zipCode}
                  />
                </Stack>
                <TextField
                  {...register('fullAddress')}
                  label={errors.fullAddress ? errors.fullAddress.message : 'Address'}
                  error={!!errors.fullAddress}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
          {participateValue !== KycFormFlag.init && residentValue !== KycFormFlag.init && (
            <Button type="submit" color="secondary" sx={{ width: '100%' }} disabled={!isSaveButtonEnabled}>
              Save
            </Button>
          )}
        </Stack>
        <Stack
          sx={{ maxWidth: { xs: '100%', md: 575 }, width: '100%' }}
          justifyContent="flex-start"
          flexDirection={{ xs: 'column-reverse', md: 'column' }}
        >
          <Typography sx={{ mt: { xs: 2, md: 0 } }}>
            In an effort to increase the utility of STARS and improve the brand association with Mogul, Mogul will be
            launching a STARS version 2 token and rebranding the token under the symbol MOGUL. As part of this
            migration, current holders must visit the migration website, connect their wallet, and initiate a
            transaction to swap their STARSv1 for STARSv2 tokens (now called MOGUL). Upon initiating the transaction the
            user will be required to agree to the terms and conditions therein. If they disagree, they will not be able
            to complete a transaction to receive their new tokens. There are no new tokens being sold in a sale in the
            process of this migration.
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
